import React, {useCallback, useEffect, useMemo, useState} from 'react';
import DocumentTitle from 'react-document-title';

import isEmpty from 'lodash.isempty';
import {InternalServerError} from 'src/components/page-not-found';
import {FilterValue} from 'src/hooks/filtered-data/useFilteredData';
import {useFilters} from 'src/hooks/useFilters';
import {usePagination} from 'src/hooks/usePagination';
import {useSortingRows} from 'src/hooks/useSortingRows';
import {clearAttributesFilters} from 'src/modules/attributes/services/filters/actions';
import {clearChangeRequestsFilters} from 'src/modules/change-requests/services/filters/actions';
import {EventsExcelDownload} from 'src/modules/events/download/events-excel-download';
import {columnCriteriaStripeData, dateCriteriaStripeData} from 'src/modules/events/filter-utils/criteriaStripeData';
import {getEventsInfo} from 'src/modules/events/services';
import {getEventsFilters} from 'src/modules/events/services/filters';
import {clearEventsFilters, setAllEventsFilters, setEventsFilter} from 'src/modules/events/services/filters/actions';
import {getFilteredEvents} from 'src/modules/events/services/selectors';
import {getAllEvents, getAllEventsFailure, getAllEventsSuccess} from 'src/openapi/apis/EventControllerApi';
import {EventBasicInfo} from 'src/openapi/models/EventBasicInfo';
import {useAppDispatch, useAppSelector} from 'src/store';
import {createColumnCriteria, createDateCriteria} from 'src/utils/criteriaStripeDataHelper';

import {Loading} from '@pb/kit-component-loading';

import {ColumnTypes} from './constants';
import {Filters, SortColumns, ValueFilterKeys} from './typedefs';
import {EventsView} from './views/events.view';

export const EventsController = () => {
    const dispatch = useAppDispatch();
    const {filters} = useFilters<Filters>(getEventsFilters, clearEventsFilters, setAllEventsFilters);
    const rows = useAppSelector(getEventsInfo);
    const [error, setError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const filteredRows = useAppSelector(getFilteredEvents);
    const {sortConfig, onSort, sortedRows} = useSortingRows<EventBasicInfo, SortColumns>(filteredRows);
    const {slicedRowsByPage, hasMore, intersectRef, isVisible} = usePagination<EventBasicInfo>(sortedRows);

    useEffect(() => {
        dispatch(clearAttributesFilters());
        dispatch(clearChangeRequestsFilters());
    }, [dispatch]);

    const clearAllFilters = useCallback(() => {
        dispatch(clearEventsFilters());
    }, [dispatch]);

    useEffect(() => {
        if (isEmpty(rows)) {
            setIsLoading(true);
            dispatch(getAllEvents()).then(e => {
                if (e.type === getAllEventsSuccess.type) {
                    setIsLoading(false);
                }
                if (e.type === getAllEventsFailure.type) {
                    setError(true);
                    setIsLoading(false);
                }
            });
        }
    }, [dispatch, rows]);

    const setFilter = useCallback((filterType: string, filterValue: FilterValue) => {
        dispatch(setEventsFilter({filterType, filterValue}));
    }, [dispatch]);

    const onPillRemove = useCallback((pillName: string, column: ColumnTypes) => {
        setFilter(column, filters[column as ValueFilterKeys].filter(value => value !== pillName));
    }, [filters, setFilter]);

    const criteriaStripeData = useMemo(() => {
        return [
            ...createColumnCriteria(filters, columnCriteriaStripeData, onPillRemove),
            ...createDateCriteria(filters, dateCriteriaStripeData, setFilter),
        ];
    }, [filters, onPillRemove, setFilter]);

    if (error) {
        return (
            <InternalServerError/>
        );
    }

    if (isEmpty(rows) && isLoading) {
        return (
            <Loading fast={true}/>
        );
    }

    return (
        <>
            <DocumentTitle title="Events"/>
            <EventsView {...{
                rows,
                setFilter,
                onSort,
                sortConfig,
                preparedRows: slicedRowsByPage,
                filters,
                hasMore,
                intersectRef,
                isVisible,
                clearAllFilters,
                criteriaStripeData,
            }}
            />
            <EventsExcelDownload filters={filters} events={sortedRows}/>
        </>
    );
};
